import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Plus,
  User,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { DeleteModal } from "../../components/modals/DeleteModal"
import { Spinner } from "../../components/common/Spinner"
import { UserControlsBar } from "../../components/controls/UserControlBar"
import { PaginationControls } from "../../components/common/PaginationControls"
import UserCard from "../../components/cards/UserCard"
import { usersApi } from "../../services/usersApi"

interface UserData {
  id: string
  name: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
}

export default function Users() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<UserData[]>([])
  
  // Search states - separate input value from actual search query
  const [searchInputValue, setSearchInputValue] = useState("")
  const [searchQuery, setSearchQuery] = useState("")

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(50)
  const [totalPages, setTotalPages] = useState<number>(0)

  // Delete modal states
  const [deleteUserModal, setDeleteUserModal] = useState(false)
  const [selectedId, setSelectedId] = useState("")

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const offset = (currentPage - 1) * recordsPerPage
      const response = await usersApi.getUsers(recordsPerPage, offset, searchQuery)
      
      // Transform UserType to User interface for display
      const transformedUsers: UserData[] = response.users.map(user => ({
        id: user.id,
        name: user.full_name || user.email,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone
      }))
      
      setUsers(transformedUsers)
      // Ensure totalPages is at least 1, even when there are no users
      setTotalPages(Math.max(1, Math.ceil(response.users_count / recordsPerPage)))
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError('Failed to load users. Please try again.')
      setUsers([])
      setTotalPages(1) // Set to 1 instead of 0 when there's an error
    } finally {
      setLoading(false)
    }
  }, [currentPage, recordsPerPage, searchQuery])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleRecordsPerPage = (value: string) => {
    const newValue = Number.parseInt(value)
    setRecordsPerPage(newValue)
    setCurrentPage(1)
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const onAddHandle = () => {
    if (!loading) {
      navigate("/app/users/add-user-modern")
    }
  }

  const selectUser = (userId: string) => {
    navigate(`/app/users/user-details`, {
      state: { userId, detail: true },
    })
  }

  const deleteUser = (deleteId: string) => {
    setDeleteUserModal(true)
    setSelectedId(deleteId)
  }

  const deleteUserModalClose = () => {
    setDeleteUserModal(false)
    setSelectedId("")
  }

  const deleteItem = async () => {
    try {
      await usersApi.deleteUser(selectedId)
      deleteUserModalClose()
      // Refresh the list after successful deletion
      fetchUsers()
    } catch (error) {
      console.error('Failed to delete user:', error)
      setError('Failed to delete user. Please try again.')
      deleteUserModalClose()
    }
  }

  // Handle search submission (when Enter is pressed or search icon is clicked)
  const handleSearchSubmit = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1) // Reset to first page when searching
  }

  // Handle search input change (just update the input value, don't trigger search)
  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value)
  }

  const recordsList = [
    { value: "10", label: "10 Records per page" },
    { value: "20", label: "20 Records per page" },
    { value: "30", label: "30 Records per page" },
    { value: "40", label: "40 Records per page" },
    { value: "50", label: "50 Records per page" },
    { value: "100", label: "100 Records per page" },
  ]

  // No frontend filtering - search is handled by the backend API

  return (
    <div className="min-h-screen bg-slate-50 pt-2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">User Management</h1>
          <p className="text-slate-600">Manage and track your system administrators efficiently</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Controls Bar */}
        <UserControlsBar
          recordsPerPage={recordsPerPage}
          onRecordsPerPageChange={handleRecordsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onAddUser={onAddHandle}
          recordsList={recordsList}
          searchQuery={searchInputValue}
          onSearchChange={handleSearchSubmit}
          onSearchInputChange={handleSearchInputChange}
          currentRecordsCount={users.length}
        />

        {/* Users Content */}
        <div className="space-y-2 md:space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : users.length > 0 ? (
            users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onSelect={selectUser}
                onDelete={deleteUser}
              />
            ))
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <div className="text-slate-400 mb-4">
                  <User className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchQuery ? 'No users found matching your search' : 'No users found'}
                </h3>
                <p className="text-slate-600 mb-4">
                  {searchQuery ? 'Try adjusting your search terms.' : 'Get started by adding your first user admin.'}
                </p>
                {!searchQuery && (
                  <Button onClick={onAddHandle} className="bg-slate-900 hover:bg-slate-800 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add User Admin
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />

        <DeleteModal
          open={deleteUserModal}
          onClose={deleteUserModalClose}
          onConfirm={deleteItem}
          title="Delete User"
          description="Are you sure you want to delete this user? This action cannot be undone."
        />
      </div>
    </div>
  )
} 